// Definice agendy – neutrální kontrakt pro business kód

import type { PresentationHints } from './navigationTypes';
import type { ParamCodecMap, ParamInput, ParamOutput } from './paramCodec';

/** Kontrakt definice jedné agendy */
export interface AgendaDefinitionConfig<
  TOpen extends ParamCodecMap = ParamCodecMap,
  TState extends ParamCodecMap = ParamCodecMap,
> {
  /** Jedinečná identita agendy v registru */
  readonly id: string;
  /** Interní cesta používaná serializerem */
  readonly path: string;
  /** Strukturální parametry nutné k otevření */
  readonly open: TOpen;
  /** Lokální URL stav agendy */
  readonly state?: TState;
  /** Titulek pro breadcrumb */
  readonly title?: string | ((params: ParamOutput<TOpen>) => string);
  /** Ikona pro breadcrumb – Lucide ikon name (volitelné, fallback na výchozí) */
  readonly icon?: string;
  /** Metadata pro shell prezentaci */
  readonly presentationHints?: PresentationHints;
}

/** Type-erased storage verze pro registr, kde neznáme konkrétní TOpen/TState */
export interface StoredAgendaConfig {
  readonly id: string;
  readonly path: string;
  readonly open: ParamCodecMap;
  readonly state?: ParamCodecMap;
  readonly title?: string | ((params: Record<string, unknown>) => string);
  readonly icon?: string;
  readonly presentationHints?: PresentationHints;
}

/** Veřejná handle na definovanou agendu – nese typ parametrů pro compile-time garance */
export interface AgendaHandle<
  TOpen extends ParamCodecMap = ParamCodecMap,
  TState extends ParamCodecMap = ParamCodecMap,
> {
  readonly id: string;
  readonly path: string;
  readonly config: AgendaDefinitionConfig<TOpen, TState>;
  /** Phantom typ – nepoužívat přímo, slouží jen pro inferenci */
  readonly _openInput: ParamInput<TOpen>;
  readonly _stateInput: ParamInput<TState>;
  readonly _openOutput: ParamOutput<TOpen>;
  readonly _stateOutput: ParamOutput<TState>;
}

/**
 * Definuje novou agendu s typově bezpečným kontraktem parametrů.
 * Agenda je neutrální vůči prezentaci (root/modal).
 */
export function defineAgenda<
  TOpen extends ParamCodecMap,
  TState extends ParamCodecMap = Record<string, never>,
>(config: AgendaDefinitionConfig<TOpen, TState>): AgendaHandle<TOpen, TState> {
  return {
    id: config.id,
    path: config.path,
    config,
    // Phantom properties – runtime hodnoty nejsou potřeba
    _openInput: undefined as unknown as ParamInput<TOpen>,
    _stateInput: undefined as unknown as ParamInput<TState>,
    _openOutput: undefined as unknown as ParamOutput<TOpen>,
    _stateOutput: undefined as unknown as ParamOutput<TState>,
  };
}

// Re-export pro pohodlí
export type { ParamCodecMap, ParamInput, ParamOutput };
